import { useState } from "react";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import moment from "moment-timezone";
import { useGetNewDevicesPdfReportMutation } from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import { getMonthStart } from "../../../utils/reportDateDefaults";

/**
 * YENİ EKLENEN CİHAZLAR RAPORU — PDF çıktısı.
 *
 * Rapor önceden ekranda tablo basıp Excel indiriyordu; 2026-09 kullanıcı
 * kararıyla belge raporlarıyla (Müşteri Performans ailesi) aynı düzene
 * taşındı: sunucu PDF üretir, sayfa yalnızca filtreyi toplar ve belgeyi
 * önizler. Yerleşim de o ailenin deseni — filtre SOLDA dar kolonda
 * (`sidebar`), belge SAĞDA (`ReportPdfPreview`).
 *
 * VERİ KAYNAĞI DEĞİŞMEDİ: aynı tarih aralığında oluşturulmuş cihazlar, aynı
 * alanlar (firma, durum, aktiflik, GSM üçlüsü, sürüm üçlüsü, eklenme).
 * PDF ek olarak dağılım ve durum × gün kırılımı gösterir; ham cihaz listesi
 * belgenin sonunda aynen durur.
 */
export default function ReportNewDevicesBetweenDatesPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // Tarih sınırları yerel saat diliminde hesaplanır — sunucu dönemi
  // Europe/Istanbul'a göre çözer, gün kayması olmasın.
  const getEndOfDay = (date) => moment(date).tz(tz).endOf("day").toDate();

  // Başlangıç varsayılanı ayın ilk günü (Workspace #154)
  const [startDate, setStartDate] = useState(() => getMonthStart(tz));
  const [endDate, setEndDate] = useState(getEndOfDay(new Date()));

  // PDF önbelleklenecek bir veri değil — query değil mutation.
  const [getReport, { data, isLoading, error, reset }] =
    useGetNewDevicesPdfReportMutation();

  const canSearch = Boolean(startDate && endDate);

  const handleSearch = () => {
    if (!canSearch) return;
    getReport({ token, startDate, endDate });
  };

  const handleClearFilters = () => {
    setStartDate(getMonthStart(tz));
    setEndDate(getEndOfDay(new Date()));
    reset?.();
  };

  const report = data?.data;
  const pdfBase64 = report?.pdfBase64;

  const errorMessage =
    error?.data?.message || error?.error || "Rapor oluşturulurken bir hata oluştu.";

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, report?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  const activeFilters = [
    {
      label: "Tarih",
      value: `${new Date(startDate).toLocaleDateString("tr-TR")} - ${new Date(
        endDate
      ).toLocaleDateString("tr-TR")}`,
    },
  ];

  return (
    <ReportLayout
      title="Yeni Eklenen Cihazlar Raporu"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Tarih Aralığı Seçimi"
          description="Yeni eklenen cihazları görüntülemek için tarih aralığını seçin"
          showDatePickers={true}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isLoading}
          searchDisabled={!canSearch}
          searchDisabledMessage="Başlangıç ve bitiş tarihlerini seçiniz"
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          activeFilters={activeFilters}
        />
      }
    >
      {isLoading && (
        <ReportLoadingState
          title="Yeni Cihazlar Raporu Hazırlanıyor"
          description="Veriler yükleniyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={4}
        />
      )}

      {!isLoading && error && (
        <ReportErrorState title="Hata Oluştu" message={errorMessage} icon="warning" />
      )}

      {!isLoading && !error && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`Yeni Eklenen Cihazlar — ${report?.dateRange || ""}`}
        />
      )}

      {!isLoading && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Rapor Bekleniyor"
          message="Tarih aralığını seçip 'Raporu Getir' butonuna basın."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
